import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import {
  getChatControlData,
  selectAllMeetings,
  setChatDialogOpen,
  setVideoChatOpen,
} from 'components/ChatDialog/store/slices/chatSlice';
import { setThreadWithUsers } from 'components/ChatDialog/store/thunks/loadThreadUsers';
import { fetchUpcomingMeetings } from 'components/ChatDialog/store/thunks/fetchUpcomingMeetings';
import { showMeetingNotification } from 'utils/browserNotifications';

// Fire the "starts soon" reminder once now is within this long of the start.
const REMINDER_LEAD_MS = 15 * 60 * 1000;
// Treat a meeting as "starting" for this long past its start, so a throttled
// background tab that skips a tick still catches it.
const START_GRACE_MS = 2 * 60 * 1000;
// How often to re-check the clock against known meetings.
const CHECK_MS = 30 * 1000;
// Safety-net re-fetch of upcoming meetings (websocket events keep them live
// between fetches; this catches anything created before we connected).
const REFRESH_MS = 10 * 60 * 1000;

/**
 * App-wide meeting reminders. Fetches the user's upcoming meetings across the
 * threads it knows, then fires a browser notification 15 minutes before each
 * meeting and again when it starts — click navigates straight into the meeting.
 *
 * Reminders only fire while the app is open in a tab (no web push); a
 * backgrounded tab still fires them. Mounted once, next to useChatNotifications.
 */
export const useMeetingNotifications = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('common');

  const meetings = useSelector(selectAllMeetings);
  const { threads } = useSelector(getChatControlData);
  const projects = useSelector((state) => state.root.projects);

  // Refs so the click handler and reminder builder always read current values
  // without re-arming the interval on every unrelated store change.
  const threadsRef = useRef(threads);
  threadsRef.current = threads;
  const projectsRef = useRef(projects);
  projectsRef.current = projects;
  const tRef = useRef(t);
  tRef.current = t;

  // Guards a reminder from firing twice: in-memory within the session, plus
  // localStorage so a reload doesn't re-fire one that already went out. Keyed by
  // start time too, so a reschedule earns a fresh reminder.
  const firedRef = useRef(new Set());

  const hasFired = (key) => {
    if (firedRef.current.has(key)) return true;
    try {
      return localStorage.getItem(`meetingNotified:${key}`) === '1';
    } catch {
      return false;
    }
  };
  const markFired = (key) => {
    firedRef.current.add(key);
    try {
      localStorage.setItem(`meetingNotified:${key}`, '1');
    } catch {
      // Private mode / quota — the in-memory guard still holds for the session.
    }
  };

  // Every thread identifier we might need to ask about. Meetings are keyed by
  // whichever of (id, threadId) the thread resolved to at create time, so we
  // send both; the backend dedupes meetings by id.
  const threadIdsKey = useMemo(() => {
    const ids = new Set();
    for (const thread of Object.values(threads || {})) {
      if (thread?.id) ids.add(thread.id);
      if (thread?.threadId) ids.add(thread.threadId);
    }
    return Array.from(ids).sort().join(',');
  }, [threads]);

  // Fetch on load once threads are known, then refresh on a slow safety net.
  useEffect(() => {
    if (!threadIdsKey) return;
    const threadIds = threadIdsKey.split(',');
    dispatch(fetchUpcomingMeetings(threadIds));
    const id = setInterval(
      () => dispatch(fetchUpcomingMeetings(threadIds)),
      REFRESH_MS
    );
    return () => clearInterval(id);
  }, [threadIdsKey, dispatch]);

  const navigateToMeeting = (meeting) => {
    const currentThreads = threadsRef.current || {};
    const thread =
      currentThreads[meeting.threadId] ||
      Object.values(currentThreads).find(
        (candidate) => (candidate.threadId || candidate.id) === meeting.threadId
      );
    if (thread) {
      dispatch(setThreadWithUsers({ thread }));
      dispatch(setChatDialogOpen(true));
      if (meeting.kind === 'VIDEO') {
        dispatch(setVideoChatOpen(true));
      }
      return;
    }
    // Fallback: open the project chat by URL when the thread isn't loaded.
    const project = (projectsRef.current || []).find(
      (candidate) => candidate.id === meeting.projectId
    );
    if (project?.projectKey) {
      const param = meeting.kind === 'VIDEO' ? 'openVideoChat' : 'openChat';
      history.push(`/project/${project.projectKey}?${param}`);
    }
  };

  const fireReminder = (meeting, phase) => {
    const translate = tRef.current;
    const title = meeting.title || translate('meeting.untitled');
    const body =
      phase === 'start'
        ? translate('meeting.notification_starting', { title })
        : translate('meeting.notification_soon', {
            title,
            time: moment(meeting.startsAt).format('HH:mm'),
          });
    showMeetingNotification(
      body,
      () => navigateToMeeting(meeting),
      `meeting-${meeting.id}-${phase}`
    );
  };

  useEffect(() => {
    if (meetings.length === 0) return;

    const check = () => {
      if (
        typeof Notification === 'undefined' ||
        Notification.permission !== 'granted'
      ) {
        return;
      }
      const now = Date.now();
      for (const meeting of meetings) {
        const start = new Date(meeting.startsAt).getTime();
        if (isNaN(start)) continue;

        const soonKey = `${meeting.id}:${meeting.startsAt}:soon`;
        if (
          now >= start - REMINDER_LEAD_MS &&
          now < start &&
          !hasFired(soonKey)
        ) {
          markFired(soonKey);
          fireReminder(meeting, 'soon');
        }

        const startKey = `${meeting.id}:${meeting.startsAt}:start`;
        if (
          now >= start &&
          now < start + START_GRACE_MS &&
          !hasFired(startKey)
        ) {
          markFired(startKey);
          fireReminder(meeting, 'start');
        }
      }
    };

    check(); // catch an already-imminent meeting the moment the app loads
    const id = setInterval(check, CHECK_MS);
    return () => clearInterval(id);
    // fireReminder/navigateToMeeting read live values through refs, so the
    // interval only needs re-arming when the meeting set itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetings]);
};
